import Spinner from '@atlaskit/spinner';
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';

import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Action, redirect } from 'redux-first-router';
import styled from 'styled-components';

import config from 'appconfig';
import { State } from 'model/State';
import { Dimensions } from 'components/visuals';
import t from 'modules/i18n/intl';
import Table from 'components/common/Table/Table';
import TableContent, { MACTableType } from './table/TableContent';
import { OrderResult } from './duck/models/orders';
import {
  fetchQuotesPost,
  ROUTE as ordersRouteType,
  route as ordersRoute,
} from './duck/actions/orders';
import { ROUTE as ordersPreselectedRouteType } from './duck/actions/ordersPreselected';
import { ROUTE as invoicesRouteType, route as invoicesRoute } from './duck/actions/invoices';
import { ROUTE as invoicesPreselectedRouteType } from './duck/actions/invoicesPreselected';
import OrderSearch from './components/OrderSearch';
import {
  getMACUserChannel,
  selectCartOrderResponse,
  selectQuotes,
  selectQuotesResponse,
  selectSwitchedUser,
} from './duck/selectors';
import Pagination from '../../components/common/Pagination';
import OrderLookup from './components/OrderLookup';
import UpdatePONumberModal from './components/UpdatePONumberModal';
import {
  HeaderContainer,
  LinkButton,
  LinksContainer,
  PageTitle,
} from './components/styledComponents';
import MACPageHeader from './components/macPageHeader';
import { DataCenterEOLSectionMessage } from 'components/navigation/top/WarningMessage';
import OrderPagination from './components/OrdersPagination';
import EditedOrderModal from './components/EditedOrderModal';

const LoadingContainer = styled.td`
  padding-top: ${Dimensions.units(7)}px;
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const TabsContainer = styled(LinksContainer)`
  margin-bottom: -${Dimensions.units(1)}px;
  border-bottom: none;
  justify-content: flex-end;
`;

const TabPanelWrapper = styled.div`
  padding-top: ${Dimensions.units(3)}px;
  width: 100%;
`;

const PaginationWrapper = styled.div`
  margin-top: ${Dimensions.units(1.25)}px;
`;

const orderCenterLinks = [
  {
    text: t('mac.orders.menu.pricing'),
    href: config.wacPurchaseLicensingUrl,
    key: 'pricing',
  },
  {
    text: t('mac.orders.menu.pay-quote'),
    href: '/purchase/my/payment',
    key: 'quote',
  },
  {
    text: t('mac.orders.menu.new-order'),
    href: config.root,
    key: 'new-order',
  },
  {
    text: t('mac.orders.menu.questions'),
    href: config.companyContact,
    key: 'questions',
  },
];

const tableHeadersOrders = [
  {
    display: 'Order #',
    key: 'orders-sen-number',
    width: 15,
  },
  {
    display: 'Created',
    key: 'orders-created',
    width: 15,
  },
  {
    display: 'Total',
    key: 'orders-total',
    width: 15,
  },
  {
    display: 'Status',
    key: 'orders-status',
    width: 15,
  },
  {
    display: 'Actions',
    key: 'orders-actions',
    width: 40,
  },
];

const activeTabs = [
  {
    tableType: MACTableType.orders,
    label: t('mac.orders.tabs.quotes'),
  },
  {
    tableType: MACTableType.invoices,
    label: t('mac.orders.tabs.invoices'),
  },
];

const Loading = ({ height }) => (
  <tr>
    <LoadingContainer height={height} colSpan={5}>
      <SpinnerWrapper>
        <Spinner size="large" />
      </SpinnerWrapper>
    </LoadingContainer>
  </tr>
);

const getActiveRouteType = (routeType: string): MACTableType => {
  switch (routeType) {
    case ordersRouteType:
    case ordersPreselectedRouteType:
      return MACTableType.orders;
    case invoicesRouteType:
    case invoicesPreselectedRouteType:
      return MACTableType.invoices;

    default:
      return MACTableType.orders;
  }
};

export type OrderCenterPaginationProps = {
  maxResult: number;
  offset: number;
  totalItems: number;
  maxCachedPages: number;
  latestPageNumber: number;
};

export type OrderSearchFiltersForPagination = {
  orderNumberMask?: number;
  technicalContactCompanyDomainName?: string;
  integerPartOfTotalAmount?: number;
};

export type OrderCenterTableItems = OrderResult[] | null;

export interface OrdersPageProps {
  orderId?: string;
  items: OrderCenterTableItems;
  isItemsLoading: boolean;
  pagination: OrderCenterPaginationProps;
  onPaginationChange: (
    params: Partial<Omit<OrderCenterPaginationProps, 'totalItems' | 'maxResult'>>,
  ) => void;
  hasMoreResults?: boolean | null;
  onSearchByFilters: (searchFilters: OrderSearchFiltersForPagination) => void;
}

export const OrdersPage = ({
  orderId,
  items,
  isItemsLoading,
  pagination,
  onPaginationChange,
  hasMoreResults,
  onSearchByFilters,
}: OrdersPageProps) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const routeType = useSelector((state: State) => state.location.type);
  const { isPartner } = useSelector((state: State) => getMACUserChannel(state));
  const isAnonymousUser = !useSelector(selectSwitchedUser);

  const activeTableType = getActiveRouteType(routeType);

  useEffect(() => {
    document.title = intl.formatMessage({ id: 'document.title.mac.orders' });
  }, []);

  const handleTabChange = (index) => {
    const redirectionRouteAction = index === 0 ? ordersRoute : invoicesRoute;
    dispatch(redirect(redirectionRouteAction() as Action));
  };

  const shouldShowPagination = pagination?.maxResult < pagination?.totalItems;
  const shouldShowOrderPagination =
    (!hasMoreResults && pagination.maxCachedPages > 1) || hasMoreResults === true;

  return (
    <>
      <MACPageHeader />
      <DataCenterEOLSectionMessage />
      <HeaderContainer bottomLine>
        <PageTitle> {t('mac.orders.title')} </PageTitle>
        <div data-cy="mac-orders-links">
          {orderCenterLinks.map((link) => (
            <LinkButton appearance="link" href={link.href} key={link.key}>
              {link.text}
            </LinkButton>
          ))}
        </div>
      </HeaderContainer>
      <TabsContainer>
        <OrderSearch
          setSearchFiltersForPagination={onSearchByFilters}
          tableType={activeTableType}
          maxResults={pagination.maxResult}
        />
      </TabsContainer>
      <Tabs
        onChange={handleTabChange}
        selected={activeTableType === MACTableType.orders ? 0 : 1}
        id="licenses-page-tabs"
      >
        <TabList>
          {activeTabs.map(({ label, tableType }) => (
            <Tab key={`${tableType}-tab`}>{label}</Tab>
          ))}
        </TabList>
        {activeTabs.map(({ tableType }) => (
          <TabPanel key={`${tableType}-table`}>
            <TabPanelWrapper>
              <Table
                body={
                  items ? (
                    <TableContent
                      items={items}
                      tableType={activeTableType}
                      isPartner={isPartner}
                      senNumber={orderId}
                    />
                  ) : (
                    (!isAnonymousUser || isItemsLoading) && (
                      <Loading height={49 * pagination.maxResult - 60} />
                    )
                  )
                }
                footers={[]}
                headers={tableHeadersOrders}
                dataAttr={activeTableType}
              />
            </TabPanelWrapper>
          </TabPanel>
        ))}
      </Tabs>
      {shouldShowPagination && ( // should be deprecated once pagination?.totalItems will be constantly null
        <PaginationWrapper data-cy="pagination">
          <Pagination
            limit={pagination.maxResult}
            offset={pagination.offset}
            totalItems={pagination.totalItems}
            onPaginationChange={onPaginationChange}
          />
        </PaginationWrapper>
      )}
      {shouldShowOrderPagination && (
        <PaginationWrapper data-cy="pagination">
          <OrderPagination
            limit={pagination.maxResult}
            offset={pagination.offset}
            maxCachedPages={pagination.maxCachedPages}
            latestPageNumber={pagination.latestPageNumber}
            onPaginationChange={onPaginationChange}
            hasMoreResults={hasMoreResults}
          />
        </PaginationWrapper>
      )}
      <OrderLookup isPartner={isPartner} />
      <UpdatePONumberModal />
      <EditedOrderModal items={items} />
    </>
  );
};

export const MACOrdersPage = (props: Pick<OrdersPageProps, 'orderId'>) => {
  const dispatch = useDispatch();
  const initialPaginationState: OrderCenterPaginationProps = {
    maxResult: 10,
    offset: 0,
    totalItems: 10, // should be deprecated after getting rid of old pagination
    maxCachedPages: 0,
    latestPageNumber: 0,
  };
  const [pagination, setPagination] = useState<OrderCenterPaginationProps>(initialPaginationState);
  const [searchFilters, setSearchFilters] = useState<OrderSearchFiltersForPagination>({});

  const updateSearchFilters = (value: OrderSearchFiltersForPagination) => {
    setSearchFilters(value);
    setPagination({ ...initialPaginationState, maxResult: pagination.maxResult });
  };

  const ordersResponse = useSelector((state) => selectQuotesResponse(state as State));
  const orders = (ordersResponse?.ifSuccess((r) => r)?.results || null) as OrderCenterTableItems;

  const singleOrderResponse = useSelector((state: State) =>
    selectCartOrderResponse(Number(props.orderId), state),
  );
  const singleOrder = singleOrderResponse?.ifSuccess((r) => r);

  const totalResults = useSelector((state) => selectQuotes(state as State)?.totalAvailable || 0);
  const hasMoreResults = useSelector((state) => selectQuotes(state as State)?.hasMoreResults);

  useEffect(() => {
    setPagination({
      ...pagination,
      totalItems: totalResults,
    });
  }, [totalResults]);

  const handlePaginationChange = (params: {
    limit?: number;
    offset?: number;
    maxCachedPages?: number;
    latestPageNumber?: number;
  }) => {
    setPagination({
      ...pagination,
      maxResult: params.limit || pagination.maxResult,
      offset: params.offset || 0,
      maxCachedPages: params.maxCachedPages || 0,
      latestPageNumber: params.latestPageNumber || 0,
    });

    dispatch(
      fetchQuotesPost(
        params.limit || pagination.maxResult,
        params.offset || 0,
        searchFilters.orderNumberMask,
        searchFilters.technicalContactCompanyDomainName,
        searchFilters.integerPartOfTotalAmount,
      ),
    );
  };

  return (
    <OrdersPage
      items={orders || (singleOrder ? [singleOrder] : null)}
      isItemsLoading={ordersResponse?.inProgress || singleOrderResponse?.inProgress}
      pagination={pagination}
      hasMoreResults={hasMoreResults}
      onPaginationChange={handlePaginationChange}
      onSearchByFilters={updateSearchFilters}
      {...props}
    />
  );
};
